<template>
  <Authenticated>
    <TrainingNav />
    <div class="content">
      <div class="header">
        <div class="title">
          <h2>{{ activeForm.title }}</h2>
          <sub>{{ activeForm.description }}</sub>
        </div>
        <div class="staff-member">
          <div class="last-update">
            <button type="button" class="last-update-trigger" @click="displayAllEdits">
              <span class="last-update-label">Last update</span>
              <span class="last-update-value">{{ lastUpdated }}</span>
            </button>
            <Transition name="dropdown">
              <div class="display-all-edits" v-if="allEdits">
                <ul>
                  <li v-for="update in updates" :key="update.date">{{ new Date(update.date).toLocaleDateString() }} by {{ update.manager }}</li>
                </ul>
              </div>
            </Transition>
          </div>
          <div class="staff-name">
            <p @click.stop.prevent="showUserDetailsModal(staffMember)">{{ staffMember.value.payrollID }}</p>
          </div>
          <div :class="percentageComplete === 100 ? 'complete' : 'completing'">
            <p>Completion: {{ percentageComplete }}%</p>
          </div>
          <div class="save">
            <button @click="saveForm">Save</button>
          </div>
        </div>
      </div>
      <div v-for="question in activeForm.questions || []" :key="question.id || question.question" class="form-data">
        <div v-if="question && question.type == 'radio'" class="radio component">
          <p>{{ question.question }}</p>
          <div v-for="option in question.options" :key="option">
            <input
              v-if="responses && question.id in responses && option == responses[question.id]"
              type="radio"
              :name="question.question"
              :value="option"
              :id="`${question.id}-${option}`"
              @change="updateSelectionAnswer(question, $event.target.value)"
              checked />
            <input v-else type="radio" :name="question.question" :value="option" :id="`${question.id}-${option}`" @change="updateSelectionAnswer(question, $event.target.value)" />
            <label :for="`${question.id}-${option}`">{{ option }}</label>
          </div>
        </div>

        <div v-if="question && question.type == 'rating'" class="rating component">
          <p>{{ question.question }}</p>
          <div class="rating-options">
            <template v-for="n in question.max" :key="n">
              <input
                type="radio"
                :name="question.question"
                :value="n"
                :id="`${question.id}-${n}`"
                :aria-label="`${question.question} ${n}`"
                :checked="String(formResponses[question.id]) === String(n)"
                @change="updateSelectionAnswer(question, $event.target.value)" />
              <label :for="`${question.id}-${n}`" class="rating-step" :class="{ selected: isRatingStepSelected(question, n) }">{{ n }}</label>
            </template>
          </div>
        </div>
        <div v-if="question && question.type == 'textarea'" class="textarea component">
          <label :for="question.id">{{ question.question }}</label>
          <textarea
            v-if="responses && question.id in responses"
            :id="question.id"
            :name="question.question"
            @input="updateInputAnswer(question, $event.target.value)"
            :value="responses[question.id]" />
          <textarea v-else :id="question.id" :name="question.question" @input="updateInputAnswer(question, $event.target.value)"></textarea>
        </div>

        <div v-if="question && question.type == 'text'" class="text component">
          <label :for="question.id">{{ question.question }}</label>
          <input
            v-if="responses && question.id in responses"
            :id="question.id"
            :name="question.question"
            :value="responses[question.id]"
            @input="updateInputAnswer(question, $event.target.value)" />
          <input v-else :id="question.id" :name="question.question" @input="updateInputAnswer(question, $event.target.value)" />
        </div>

        <div v-if="question && question.type == 'checkbox'" class="checkbox component">
          <p>{{ question.question }}</p>
          <div v-for="option in question.options" :key="option">
            <input
              v-if="responses && question.id in responses && responses[question.id].includes(option)"
              type="checkbox"
              :name="question.question"
              :value="option"
              :id="`${question.id}-${option}`"
              @change="updateCheckboxAnswer(question, $event)"
              checked />
            <input v-else type="checkbox" :name="question.question" :value="option" :id="`${question.id}-${option}`" @change="updateCheckboxAnswer(question, $event)" />
            <label :for="`${question.id}-${option}`">{{ option }}</label>
          </div>
        </div>

        <div v-if="question && question.type == 'checklist'" class="checklist component">
          <p>{{ question.question }}</p>
          <div v-for="option in question.options" :key="option">
            <input
              v-if="responses && question.id in responses && responses[question.id].includes(option)"
              type="checkbox"
              :name="question.question"
              :value="option"
              :id="`${question.id}-${option}`"
              @change="updateChecklistAnswer(question, $event)"
              checked />
            <input v-else type="checkbox" :name="question.question" :value="option" :id="`${question.id}-${option}`" @change="updateChecklistAnswer(question, $event)" />
            <label :for="`${question.id}-${option}`">{{ option }}</label>
          </div>
        </div>

        <div v-if="question && question.type == 'grid'" class="grid component">
          <p>{{ question.question }}</p>
          <div class="grid-table-wrapper">
            <table class="grid-table">
              <colgroup>
                <col class="col-label" />
                <col v-for="col in question.columns" :key="col" class="col-cell" />
              </colgroup>
              <thead>
                <tr>
                  <th></th>
                  <th v-for="col in question.columns" :key="col">{{ col }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in question.rows" :key="row">
                  <td class="grid-row-label">{{ row }}</td>
                  <td v-for="col in question.columns" :key="col" class="grid-cell">
                    <input
                      v-if="question.gridType === 'radio'"
                      type="radio"
                      :name="`${question.id}-${row}`"
                      :value="col"
                      :id="`${question.id}-${row}-${col}`"
                      :checked="formResponses[question.id] && formResponses[question.id][row] === col"
                      @change="updateGridRadioAnswer(question, row, col)" />
                    <input
                      v-else
                      type="checkbox"
                      :name="`${question.id}-${row}`"
                      :value="col"
                      :id="`${question.id}-${row}-${col}`"
                      :checked="formResponses[question.id] && Array.isArray(formResponses[question.id][row]) && formResponses[question.id][row].includes(col)"
                      @change="updateGridCheckboxAnswer(question, row, col, $event)" />
                    <label :for="`${question.id}-${row}-${col}`"></label>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    <UserDetailsModal v-if="detailsForModal" :isOpen="isOpen" :staff="detailsForModal" @close="closeUserDetails" @confirm="saveData" />
  </Authenticated>
</template>

<script>
import Authenticated from "../_layouts/Authenticated.vue";
import TrainingNav from "./TrainingNav.vue";
import UserDetailsModal from "./UserDetailsModal.vue";
import { mapGetters } from "vuex";
export default {
  name: "ActiveForm",
  components: { Authenticated, TrainingNav, UserDetailsModal },
  data() {
    return {
      formResponses: {},
      updates: [],
      allEdits: false,
      isOpen: false,
      detailsForModal: null,
    };
  },

  mounted() {
    if (!this.isLoaded || !this.activeForm || !this.staffMember) {
      this.$router.push("/training/dashboard");
      return;
    }
    if (this.responses) {
      this.formResponses = { ...this.responses };
      this.updates = this.staffMember.value.training.find((f) => f.formId === this.activeForm._id).updatedAt || [];
    }
  },

  methods: {
    showUserDetailsModal(staff) {
      this.detailsForModal = staff;
      this.isOpen = true;
    },

    closeUserDetails() {
      this.isOpen = false;
    },

    saveData(updatedStaff) {
      this.$store.dispatch("training/updateStaff", updatedStaff);
      this.staffMember = updatedStaff;
    },

    displayAllEdits() {
      this.allEdits = !this.allEdits;
    },

    updateInputAnswer(question, value) {
      this.formResponses[question.id] = value;
    },

    updateCheckboxAnswer(question, value) {
      if (!Array.isArray(this.formResponses[question.id])) {
        this.formResponses[question.id] = [];
      }
      if (value.target.checked) {
        this.formResponses[question.id] = [...this.formResponses[question.id], value.target.value];
      } else {
        this.formResponses[question.id] = this.formResponses[question.id].filter((o) => o !== value.target.value);
      }
    },

    updateChecklistAnswer(question, value) {
      if (!Array.isArray(this.formResponses[question.id])) {
        this.formResponses[question.id] = [];
      }
      if (value.target.checked) {
        this.formResponses[question.id] = [...this.formResponses[question.id], value.target.value];
      } else {
        this.formResponses[question.id] = this.formResponses[question.id].filter((o) => o !== value.target.value);
      }
    },

    updateSelectionAnswer(question, value) {
      this.formResponses[question.id] = value;
    },

    updateGridRadioAnswer(question, row, col) {
      if (!this.formResponses[question.id] || typeof this.formResponses[question.id] !== "object") {
        this.formResponses[question.id] = {};
      }
      this.formResponses[question.id] = { ...this.formResponses[question.id], [row]: col };
    },

    updateGridCheckboxAnswer(question, row, col, event) {
      if (!this.formResponses[question.id] || typeof this.formResponses[question.id] !== "object") {
        this.formResponses[question.id] = {};
      }
      const rowAnswers = Array.isArray(this.formResponses[question.id][row]) ? [...this.formResponses[question.id][row]] : [];
      if (event.target.checked) {
        if (!rowAnswers.includes(col)) rowAnswers.push(col);
      } else {
        const i = rowAnswers.indexOf(col);
        if (i !== -1) rowAnswers.splice(i, 1);
      }
      this.formResponses[question.id] = { ...this.formResponses[question.id], [row]: rowAnswers };
    },

    isRatingStepSelected(question, value) {
      const selected = Number(this.formResponses[question.id]);
      if (Number.isNaN(selected)) return false;
      return value <= selected;
    },

    saveForm() {
      let update =
        this.percentageComplete === 100
          ? { date: new Date().toISOString(), completed: true, manager: this.getManager.value.firstName + " " + this.getManager.value.lastName }
          : {
              date: new Date().toISOString(),
              completed: false,
              manager: this.getManager.value.firstName + " " + this.getManager.value.lastName,
              percentageComplete: this.percentageComplete,
            };
      this.updates.push(update);
      let form = {
        formId: this.activeForm._id,
        responses: this.formResponses,
        updatedAt: this.updates,
      };
      this.$store.dispatch("training/updateStaffTraining", { staffID: this.staffMember.value._id, trainingForm: form });
      this.$router.push("/training/dashboard");
    },
  },
  computed: {
    ...mapGetters("training", ["getActiveForm", "getStaffMember", "getManager", "isLoaded"]),

    lastUpdated() {
      if (!this.updates || this.updates.length === 0) return "Not started";
      const lastUpdate = this.updates[this.updates.length - 1];
      return new Date(lastUpdate.date).toLocaleDateString() + " by " + lastUpdate.manager;
    },

    activeForm() {
      return this.$route.params.formID ? this.getActiveForm(this.$route.params.formID) : this.getActiveForm();
    },

    staffMember() {
      return this.$route.params.staffID ? this.getStaffMember(this.$route.params.staffID) : this.getStaffMember();
    },

    responses() {
      if (!this.staffMember || !this.activeForm) return false;
      const resp = this.staffMember.value.training.find((f) => f.formId === this.activeForm._id) || {};
      return resp.responses || false;
    },

    percentageComplete() {
      const questions = this.activeForm.questions;
      const total = questions.reduce((sum, q) => (["checklist"].includes(q.type) ? sum + q.options.length : q.type === "grid" ? sum + (q.rows || []).length : sum + 1), 0) || 1;
      const answered = questions.reduce((sum, q) => {
        const resp = this.formResponses[q.id];
        if (["checklist"].includes(q.type)) {
          return sum + (Array.isArray(resp) ? resp.length : 0);
        }
        if (q.type === "grid") {
          if (!resp || typeof resp !== "object") return sum;
          return (
            sum +
            (q.rows || []).filter((row) => {
              const r = resp[row];
              return q.gridType === "radio" ? r !== undefined && r !== "" : Array.isArray(r) && r.length === (q.columns || []).length;
            }).length
          );
        }
        return sum + (resp !== undefined && resp !== "" ? 1 : 0);
      }, 0);
      return Math.round((answered / total) * 100);
    },
  },
  watch: {
    responses(val) {
      if (val) this.formResponses = { ...val };
    },
  },
};
</script>

<style scoped lang="scss">
.content {
  background-color: var(--primary-colour);
  padding: 1rem;
  margin: 1rem;
  border-radius: 0.5rem;
  box-shadow: var(--box-shadow);

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 1rem;
    .title {
      h2 {
        text-transform: capitalize;
        margin: 0;
      }
      sub {
        color: var(--action-colour);
      }
    }
    .staff-member {
      display: flex;
      justify-content: space-evenly;
      align-items: center;
      gap: 1rem;

      .last-update {
        position: relative;

        .last-update-trigger {
          display: flex;
          flex-direction: column;
          align-items: center;
          border: 1px solid var(--action-colour);
          border-radius: 0.25rem;
          background: transparent;
          font-size: 0.85rem;
          padding: 0.5rem 1rem;
          margin: 0;
          text-align: center;
          cursor: pointer;
          .last-update-label {
            font-weight: 600;
          }

          .last-update-value {
            display: -webkit-box;
            line-clamp: 2;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            max-width: 13rem;
            line-height: 1.2;
          }
        }

        .display-all-edits {
          position: absolute;
          top: calc(100% + 0.4rem);
          left: 0;
          z-index: 20;
          min-width: 16rem;
          max-width: 22rem;
          padding: 0.5rem 0.65rem;
          border-radius: 0.5rem;
          color: var(--action-colour);
          border: 1px solid var(--border-colour);
          background: var(--secondary-colour);
          box-shadow: var(--box-shadow);
          ul {
            margin: 0;
            padding-left: 1rem;

            li {
              margin: 0.2rem 0;
            }
          }
        }
      }

      .dropdown-enter-active,
      .dropdown-leave-active {
        transition: opacity 0.2s ease, transform 0.2s ease;
      }

      .dropdown-enter-from,
      .dropdown-leave-to {
        opacity: 0;
        transform: translateY(-6px);
      }

      .dropdown-enter-to,
      .dropdown-leave-from {
        opacity: 1;
        transform: translateY(0);
      }

      .staff-name {
        background-color: var(--secondary-colour);
        color: var(--action-colour);
        border: 1px solid var(--secondary-colour);
        padding: 1rem;
        border-radius: 0.25rem;
        cursor: pointer;
        p {
          margin: 0;
        }
      }
      .completing {
        background-color: transparent;
        color: var(--text-colour);
        border: 1px solid var(--text-colour);
        padding: 1rem;
        border-radius: 0.25rem;
        p {
          margin: 0;
        }
      }
      .complete {
        border: 1px solid var(--success-colour);
        color: var(--success-colour);
        border-radius: 0.25rem;
        padding: 1rem;
        p {
          margin: 0;
        }
      }
      .save {
        button {
          background-color: var(--success-colour);
          border: 1px solid var(--success-colour);
          padding: 1rem;
          margin: auto;
          border-radius: 0.25rem;
          cursor: pointer;
          box-shadow: var(--box-shadow);
        }
      }
    }
  }

  .form-data {
    display: flex;
    margin: 1rem;
    gap: 0.5rem;
    .component {
      display: flex;
      flex-direction: column;
      background-color: var(--secondary-colour);
      border-radius: 0.25rem;
      padding: 1rem;
      width: 100%;
      label {
        margin: 0.25rem 0;
      }
      p {
          margin: 0.25rem 0;
      }
    }
    .grid {
      .grid-table-wrapper {
        overflow-x: auto;
        margin-top: 0.5rem;
        border-radius: 0.75rem;
        overflow: hidden;
        box-shadow: var(--box-shadow);
      }
      .grid-table {
        width: 100%;
        border-collapse: collapse;
        table-layout: fixed;
        font-size: 0.9rem;
        .col-label {
          width: 30%;
        }
        .col-cell {
          width: auto;
        }
        th,
        td {
          padding: 0.6rem 1rem;
          border: 1px solid var(--border-colour);
          text-align: center;
          color: var(--text-colour);
          word-wrap: break-word;
          white-space: normal;
          overflow: hidden;
        }
        th {
          background-color: var(--primary-colour);
          font-weight: 600;
        }
        tr:first-child th:first-child {
          border-top-left-radius: 0.75rem;
        }
        tr:first-child th:last-child {
          border-top-right-radius: 0.75rem;
        }
        tr:last-child td:first-child {
          border-bottom-left-radius: 0.75rem;
        }
        tr:last-child td:last-child {
          border-bottom-right-radius: 0.75rem;
        }
        .grid-row-label {
          text-align: left;
          font-weight: 500;
          background-color: var(--primary-colour);
        }
        .grid-cell {
          input[type="radio"],
          input[type="checkbox"] {
            cursor: pointer;
            width: 1rem;
            height: 1rem;
          }
          label {
            margin: 0;
          }
        }
      }
    }

    .text,
    .textarea {
      input,
      textarea {
        border: 1px solid var(--secondary-colour);
        box-shadow: var(--box-shadow);
        border-radius: 0.25rem;
        margin: 0.25rem;
        padding: 0.5rem;
      }
    }

    .rating {
      .rating-options {
        display: flex;
        align-items: center;
        gap: 0.4rem;
        flex-wrap: wrap;
        margin-top: 0.5rem;

        input[type="radio"] {
          position: absolute;
          opacity: 0;
          width: 0;
          height: 0;
          pointer-events: none;
        }

        .rating-step {
          min-width: 2rem;
          height: 2rem;
          border-radius: 0.3rem;
          border: 1px solid var(--border-colour);
          background-color: var(--primary-colour);
          color: var(--text-colour);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          user-select: none;
          transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
        }

        .rating-step.selected {
          border-color: var(--action-colour);
          background-color: var(--action-colour);
          color: var(--primary-colour);
        }
      }
    }
  }
}
</style>
